/**
 * useWoSampleUpload — orchestrates the work-order sample upload (2026-09-10):
 *
 *   WoUploadModal (pick + review a CSV / intake workbook / KeyLAB form)
 *     → normalized rows
 *     → reconcile against the work order (server dry run)
 *     → WoSampleReconcileDialog (per-test match, exclusions, acknowledgement)
 *     → apply → reload grid + refresh the schedule banner (drift cue).
 *
 * At project level with no work order yet, a standalone WO is created first
 * and the rows applied straight away (nothing to reconcile against — TASK-019
 * bootstrap kept). Returned as render slots so WorkOrdersTabContent stays
 * under the 600-line cap.
 */
import { createElement, useCallback, useState, type ReactNode } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { createStandaloneWorkOrder } from '../../services/workOrdersTabService';
import {
  applyWorkOrderSampleUpload,
  reconcileWorkOrderSampleUpload,
  SCHEDULE_IMPACT_UNACKNOWLEDGED,
} from '../../services/workOrderSampleUploadService';
import { apiErrorText } from '../../utils/apiErrorText';
import type {
  WoSampleUploadApplyResult, WoSampleUploadReconcileResult, WoSampleUploadRowInput,
} from '../../types/workOrderSampleUpload';
import { WoUploadModal } from './WoUploadModal';
import { WoSampleReconcileDialog } from './WoSampleReconcileDialog';

type Snackbar = { open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' };

interface Args {
  /** The selected work order's key (route param); undefined at project level. */
  workOrderId: string | undefined;
  projectKey: string;
  projectId: string;
  navigate: NavigateFunction;
  setSnackbar: (s: Snackbar) => void;
  loadWorkOrder: () => Promise<void>;
  /** Schedule banner refresh — the drift cue after lines change (G2-4). */
  refetchImpact: () => Promise<unknown>;
}

interface Result {
  openUploadModal: () => void;
  /** Render once alongside the grid: the upload modal + the reconcile dialog. */
  uploadDialogs: ReactNode;
}

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Snackbar copy for a successful apply — exported for vitest. */
export function describeApplyResult(r: WoSampleUploadApplyResult): { message: string; severity: 'success' | 'warning' } {
  const parts: string[] = [];
  if (r.filled > 0) parts.push(`${plural(r.filled, 'placeholder row')} replaced`);
  if (r.created > 0) parts.push(`${plural(r.created, 'test line')} added`);
  if (r.alreadyPresent > 0) parts.push(`${r.alreadyPresent} already on the work order`);
  if (r.placeholdersDropped > 0) parts.push(`${plural(r.placeholdersDropped, 'unfilled placeholder')} removed`);
  const made: string[] = [];
  if (r.samplesCreated > 0) made.push(plural(r.samplesCreated, 'sample'));
  if (r.locationsCreated > 0) made.push(plural(r.locationsCreated, 'location'));
  let message = `${r.workOrderId}: ${parts.join(', ') || 'no changes'}${made.length ? ` · ${made.join(', ')} created` : ''}.`;
  if (r.placeholderTests.length > 0) {
    message += ` No digital module yet for: ${r.placeholderTests.join(', ')} — created as placeholder tests.`;
  }
  if (r.unresolvedTests.length > 0) {
    message += ` Skipped (no matching lab test): ${r.unresolvedTests.join(', ')}.`;
  }
  if (r.scheduleImpact) {
    message += ' The schedule no longer matches these samples — it now shows Out of sync; use "Sync now" above.';
  }
  return { message, severity: r.scheduleImpact || r.unresolvedTests.length > 0 ? 'warning' : 'success' };
}

export function useWoSampleUpload({
  workOrderId, projectKey, projectId, navigate, setSnackbar, loadWorkOrder, refetchImpact,
}: Args): Result {
  const [modalOpen, setModalOpen] = useState(false);
  const [rows, setRows] = useState<WoSampleUploadRowInput[] | null>(null);
  const [plan, setPlan] = useState<WoSampleUploadReconcileResult | null>(null);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const closeReconcile = useCallback(() => {
    setRows(null); setPlan(null); setExcluded(new Set()); setDialogError(null);
  }, []);

  const finish = useCallback(async (result: WoSampleUploadApplyResult) => {
    const { message, severity } = describeApplyResult(result);
    setSnackbar({ open: true, message, severity });
    await loadWorkOrder();
    await refetchImpact();
  }, [setSnackbar, loadWorkOrder, refetchImpact]);

  /** WoUploadModal handed us normalized rows. */
  const handleRows = useCallback(async (incoming: WoSampleUploadRowInput[]) => {
    if (!workOrderId) {
      // TASK-019: no work order yet — bootstrap one and apply directly (a fresh
      // WO has nothing to reconcile against; every row is new by definition).
      const newWo = await createStandaloneWorkOrder({ projectKey, workOrderName: 'Imported from upload' });
      const result = await applyWorkOrderSampleUpload(newWo.WorkOrderKey, incoming, { acknowledgeScheduleImpact: true });
      const { message, severity } = describeApplyResult(result);
      setSnackbar({ open: true, message, severity });
      navigate(ROUTES.WORK_ORDER_DETAIL.replace(':projectId', projectId).replace(':workOrderId', String(newWo.WorkOrderKey)));
      return;
    }
    const first = await reconcileWorkOrderSampleUpload(workOrderId, incoming);
    setRows(incoming);
    setExcluded(new Set());
    setPlan(first);
  }, [workOrderId, projectKey, projectId, navigate, setSnackbar]);

  const handleToggleInstance = useCallback(async (instanceId: string, included: boolean) => {
    if (!workOrderId || !rows) return;
    const next = new Set(excluded);
    if (included) next.delete(instanceId); else next.add(instanceId);
    setExcluded(next);
    setRefreshing(true);
    setDialogError(null);
    try {
      setPlan(await reconcileWorkOrderSampleUpload(workOrderId, rows, { excludedInstanceIds: [...next] }));
    } catch (err) {
      setDialogError(apiErrorText(err, 'Could not refresh the match.'));
    } finally {
      setRefreshing(false);
    }
  }, [workOrderId, rows, excluded]);

  const handleConfirm = useCallback(async (opts: { dropUnfilledPlaceholders: boolean; acknowledgeScheduleImpact: boolean }) => {
    if (!workOrderId || !rows) return;
    setApplying(true);
    setDialogError(null);
    try {
      const result = await applyWorkOrderSampleUpload(workOrderId, rows, { ...opts, excludedInstanceIds: [...excluded] });
      closeReconcile();
      await finish(result);
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setDialogError(code === SCHEDULE_IMPACT_UNACKNOWLEDGED
        ? 'The match changed — review the schedule impact above and confirm again.'
        : apiErrorText(err, 'Import failed. Nothing was saved.'));
    } finally {
      setApplying(false);
    }
  }, [workOrderId, rows, excluded, closeReconcile, finish]);

  const uploadDialogs = createElement(
    'div', null,
    createElement(WoUploadModal, {
      open: modalOpen,
      onClose: () => setModalOpen(false),
      onImport: handleRows,
      hasWorkOrder: Boolean(workOrderId),
    }),
    createElement(WoSampleReconcileDialog, {
      open: plan !== null,
      plan,
      refreshing,
      applying,
      error: dialogError,
      excludedInstanceIds: excluded,
      onToggleInstance: handleToggleInstance,
      onConfirm: handleConfirm,
      onCancel: closeReconcile,
    }),
  );

  return { openUploadModal: () => setModalOpen(true), uploadDialogs };
}
